import { Injectable } from '@angular/core';
import { State, Action, Selector, StateContext } from '@ngxs/store';
import {
  DockItemCreate,
  DockItemDelete,
  DockItemInitialState,
  DockItemLoadQr,
  DockItemQrInitialState,
  DockItemSetId,
  DockItemUpdate,
} from './dock-item.actions';
import { DockItemService } from './dock-item.service';
import { catchError, map, take } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { Error, CustomHttpErrorResponse, NotificationService } from '../../../../../common';
import { NavigateUserService } from '../../../../services/navigate-user.service';
import { DockListStateModel } from '../list/dock-list.state';
import { DockListLoad } from '../list/dock-list.actions';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface DockItemStateModel {
  id: number;
  qrCode: string;
}

const initialState: DockItemStateModel = {
  id: null,
  qrCode: null,
};

@State<DockItemStateModel>({
  name: 'dockItem',
  defaults: initialState,
})
@Injectable()
export class DockItemState {
  constructor(
    private dockItemService: DockItemService,
    private notificationService: NotificationService,
    private navigateUserService: NavigateUserService,
  ) {}

  @Selector()
  static getState(state: DockItemStateModel): DockItemStateModel {
    return state;
  }

  @Selector()
  static getDockId(state: DockItemStateModel): number {
    return state.id;
  }

  @Selector()
  static getDockQr(state: DockItemStateModel): string {
    return state.qrCode;
  }

  @Action(DockItemSetId)
  setDockId(ctx: StateContext<DockItemStateModel>, { payload }: DockItemSetId): void {
    ctx.patchState({ id: payload });
  }

  @Action(DockItemCreate)
  createNewDock(
    ctx: StateContext<DockItemStateModel>,
    { payload }: DockItemCreate,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    const { hubId, compartmentId, dockOverview } = payload;

    return this.dockItemService.createNewDock(hubId, compartmentId, dockOverview).pipe(
      take(1),
      map(() => {
        // todo: create routing dictionary constant
        this.notificationService.success('api.dock.success.create');
        this.navigateToDockList(hubId, compartmentId);
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(DockItemUpdate)
  updateDockOverview(
    ctx: StateContext<DockItemStateModel>,
    { payload }: DockItemUpdate,
  ): Observable<MatSnackBarRef<SimpleSnackBar>> {
    return this.dockItemService.updateDock(payload).pipe(
      take(1),
      map(() => this.notificationService.success('api.dock.success.update')),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(DockItemLoadQr)
  getDockQr(
    ctx: StateContext<DockItemStateModel>,
    { payload }: DockItemLoadQr,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | DockItemStateModel> {
    return this.dockItemService.getDockQr(payload).pipe(
      take(1),
      map((qrCode: string) => ctx.patchState({ qrCode })),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(DockItemDelete)
  deleteDocks(
    ctx: StateContext<DockListStateModel>,
    { payload }: DockItemDelete,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    const { hubId, compartmentId, dockIds, dialog, listRefreshNeeded } = payload;

    return this.dockItemService.deleteDocks(hubId, compartmentId, dockIds).pipe(
      map(() => {
        dialog.close();
        this.notificationService.success('api.dock.success.delete');

        if (listRefreshNeeded) {
          ctx.dispatch(
            new DockListLoad({
              hubId,
              compartmentId,
              searchCriteria: null,
            }),
          );
        } else {
          this.navigateToDockList(hubId, compartmentId);
        }
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(DockItemQrInitialState)
  qrSetInitialState(ctx: StateContext<DockItemInitialState>): void {
    ctx.patchState({ qrCode: null });
  }

  @Action(DockItemInitialState)
  setInitialState(ctx: StateContext<DockItemInitialState>): void {
    ctx.setState(initialState);
  }

  private navigateToDockList(hubId: number, compartmentId: number): void {
    this.navigateUserService.navigateUserToAbsolutePath(
      `/dashboard/administration/hubs/${hubId}/compartments/${compartmentId}/docks`,
    );
  }
}
